// GuidePresence.tsx — the guide moment as a single consistent card: an image of
// Craig on the LEFT, the caption + (rotated) quote on the RIGHT. One layout for
// every hook. Tap anywhere to close — it accompanies, never gates. Treated as a
// step: it records its impression on show and "continue" proceeds. Silent; text
// only; a still today (an animated clip is a drop-in replacement at the same path).
import React from 'react';
import { Icon } from '@/components/icons';
import { clipUrl } from '@/data/guideMessages';
import type { GuideSelection } from '@/services/guidePresence';

export function GuidePresence({
  selection,
  onShown,
  onClose,
  plain = false,
}: {
  selection: GuideSelection;
  /** Fired once when the moment is shown — the parent commits the impression. */
  onShown?: () => void;
  /** Tap-to-close / continue. Optional in `plain` mode (the card is static). */
  onClose?: () => void;
  /** Render as a static card (no tap-to-continue) — for surfaces that already have
   *  their own continue control, e.g. the chapter-complete celebration (H8). */
  plain?: boolean;
}) {
  React.useEffect(() => {
    onShown?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cardStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    width: '100%',
    textAlign: 'left',
    border: '1px solid var(--hair)',
    borderRadius: 18,
    overflow: 'hidden',
    background: 'linear-gradient(180deg, var(--card2-top, var(--card-top)), var(--card2-bot, var(--card-bot)))',
    boxShadow: 'var(--edge), var(--sh1)',
    animation: 'fade .35s ease',
  };

  const Inner = (
    <>
      {/* image of Craig — left. Transparent PNG: NO background (the tile shows
          through), and object-fit contain so the full width shows (hands aren't
          clipped). */}
      <img
        src={clipUrl(selection.clipId)}
        alt=""
        aria-hidden="true"
        style={{
          flexShrink: 0,
          width: 124,
          height: 152,
          alignSelf: 'center',
          objectFit: 'contain',
          objectPosition: 'center',
          display: 'block',
        }}
      />
      {/* caption + quote — right */}
      <span style={{ flex: 1, minWidth: 0, padding: '15px 14px 14px 4px' }}>
        <span style={{ display: 'block', fontFamily: 'Fraunces, Georgia, serif', fontSize: 16.5, lineHeight: 1.4, color: 'var(--ink)' }}>
          {selection.caption}
        </span>
        {selection.quote && (
          <span
            style={{
              display: 'block',
              marginTop: 11,
              paddingLeft: 12,
              borderLeft: '2.5px solid var(--green)',
              fontFamily: 'Fraunces, Georgia, serif',
              fontStyle: 'italic',
              fontWeight: 400,
              fontSize: 16.5,
              lineHeight: 1.5,
              color: 'var(--green-deep)',
            }}
          >
            “{selection.quote.text}”
          </span>
        )}
        {!plain && (
          <span style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 11, color: 'var(--ink-soft)', opacity: 0.7 }}>
            <span style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11.5, fontWeight: 600 }}>Tap to continue</span>
            <Icon name="chevRight" size={14} />
          </span>
        )}
      </span>
    </>
  );

  // Static surfaces (the celebration) render a plain card; everywhere else the
  // whole card is a tap-to-continue button.
  if (plain) {
    return <div style={cardStyle}>{Inner}</div>;
  }
  return (
    <button type="button" onClick={onClose} aria-label="Continue" style={{ ...cardStyle, cursor: 'pointer' }}>
      {Inner}
    </button>
  );
}
